// KAN-1016: настройка zod ДО всего остального кода приложения.
//
// Этот файл Next выполняет раньше гидрации и раньше любых компонентов — именно
// поэтому конфигурация живёт здесь, а не в `app/root-content.tsx`, где она
// стояла сначала. Разница не теоретическая: `allowsEval` у zod мемоизируется
// при ПЕРВОМ обращении, и схема, разобранная до вызова `config`, успевает
// выполнить пробу `Function("")` — то есть ровно то нарушение CSP, ради
// которого всё и делалось.
//
// Как это выяснилось: после первой правки в консоли прода осталось ровно одно
// нарушение из тринадцати. `ReportingObserver` с `buffered: true` (в отличие от
// слушателя `securitypolicyviolation`, который на старте ещё не подписан) отдал
// накопленный отчёт с точным адресом: чанк zod, колонка 295927 — это и есть
// `return Function("")` внутри `allowsEval`.
import "@/shared/lib/zod-config";

import * as Sentry from "@sentry/nextjs";

import { isStagingNetworkNoise } from "@/shared/api/should-report-error";
import { isForeignStack } from "@/shared/lib/sentry-foreign-frames";
import { isSignalRTransientNoise } from "@/shared/lib/signalr/transient-noise";
import { checkIsTauri } from "@/shared/lib/tauri";

const dsn = process.env["NEXT_PUBLIC_GLITCHTIP_DSN"];
const appEnv = process.env["NEXT_PUBLIC_APP_ENV"];
const buildId = process.env["NEXT_PUBLIC_BUILD_ID"];

Sentry.init({
  dsn,
  // Гейт по наличию DSN: он запекается в бандл при сборке (build-arg из
  // config/{production,home-staging} в deploy-скриптах), в dev его нет.
  // NEXT_PUBLIC_APP_ENV теперь инлайнится в клиентский бандл (env-блок
  // next.config + ARG в Dockerfile) → тег environment корректен (production /
  // home-staging). `|| "production"` ловит и пустую строку (а не только undefined).
  enabled: !!dsn,
  environment: appEnv || "production",
  release: buildId,

  // Performance-мониторинг: 5% браузерных транзакций (pageload/navigation) —
  // только на проде. На тесте перф-трассы не нужны и зря грузят GlitchTip-Postgres
  // на 512MB. browserTracingIntegration включается автоматически при rate > 0.
  tracesSampleRate: appEnv === "production" ? 0.05 : 0,
  // KAN-1318: без спанов на каждый ресурс и каждую performance-метку. В конце загрузки
  // Sentry заводит по спану на все 150+ скриптов/картинок страницы, и каждый перезапускает
  // таймер транзакции — в сэмплированной загрузке это ~0,9 с главного потока. Web Vitals
  // (LCP, CLS, INP) и спаны запросов считаются отдельно и остаются.
  integrations: [
    Sentry.browserTracingIntegration({
      ignoreResourceSpans: [
        "resource.script",
        "resource.link",
        "resource.css",
        "resource.img",
        "resource.image",
        "resource.font",
        "resource.other",
      ],
      ignorePerformanceApiSpans: [/.*/],
    }),
  ],
  // Replay не нужен
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  beforeSend(event, hint) {
    const error = hint?.originalException;

    // Фильтруем AbortError (отменённые запросы) — не баги
    if (error instanceof Error && error.name === "AbortError") {
      return null;
    }
    // axios «Request aborted» — браузер оборвал запрос сам (уход со страницы, бот
    // закрыл вкладку). Таймаут несёт тот же код, но другой текст и остаётся (GlitchTip 141/146).
    if (error instanceof Error && error.message === "Request aborted") {
      return null;
    }

    // KAN-259: таймауты и 502/504 тест-стенда — состояние VM, не баг. Здесь, а не
    // в error-handler: сюда же приходят неперехваченные промисы. На проде шлются.
    if (isStagingNetworkNoise(appEnv, error)) {
      return null;
    }

    // Фильтруем NetworkError / Failed to fetch — шум при слабом соединении
    if (error instanceof Error) {
      const msg = error.message.toLowerCase();
      if (
        msg.includes("failed to fetch") ||
        msg.includes("networkerror") ||
        msg.includes("network error") ||
        msg.includes("load failed") ||
        msg.includes("fetch failed")
      ) {
        return null;
      }
    }

    // Фильтруем ChunkLoadError / dynamic import — транзиент после деплоя (старые
    // чанки удалены при выходе нового билда). global-error.tsx ловит и авто-
    // релоадит → пользователь получает новую версию. В GlitchTip это только шум.
    if (error instanceof Error) {
      const m = error.message.toLowerCase();
      if (
        error.name === "ChunkLoadError" ||
        m.includes("failed to load chunk") ||
        m.includes("loading chunk") ||
        m.includes("failed to fetch dynamically imported module") ||
        m.includes("importing a module script failed")
      ) {
        return null;
      }
    }

    // Фильтруем инъецированный обфусцированный код (расширения браузера,
    // userscript'ы, malware): идентификаторы вида _0x5bc236 / a0_0x3b27 в файле
    // <anonymous>. Это не наш код — Next.js/Turbopack так не минифицирует.
    // (GlitchTip issue #10: "ReferenceError: a0_0x3b27 is not defined")
    const obfuscated = /_0x[0-9a-f]{3,}/i;
    if (error instanceof Error && obfuscated.test(error.message)) {
      return null;
    }
    if (
      event.exception?.values?.some((v) =>
        v.stacktrace?.frames?.some((f) => obfuscated.test(f.function ?? "")),
      )
    ) {
      return null;
    }

    // Фильтруем ResizeObserver loop — браузерный шум, не наш код
    if (
      event.exception?.values?.some((v) =>
        v.value?.toLowerCase().includes("resizeobserver loop"),
      )
    ) {
      return null;
    }

    // Фильтруем ошибки из браузерных расширений
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (
      frames?.some(
        (f) =>
          f.filename?.includes("chrome-extension://") ||
          f.filename?.includes("moz-extension://") ||
          f.filename?.includes("safari-extension://"),
      )
    ) {
      return null;
    }

    // Фильтруем всё, что упало целиком в чужом коде: инжект расширения или
    // юзерскрипт не обязан жить на extension:// и не обязан быть обфусцирован
    // — оба фильтра выше его пропускают. Судим по происхождению кадров.
    if (isForeignStack(event)) {
      return null;
    }

    // KAN-600: обрыв рукопожатия SignalR — не ошибка приложения, клиент
    // переподключается сам. Места старта соединений это уже учитывают, но при
    // авто-реконнекте ошибка всплывает из обработчика входящего сообщения,
    // мимо start().catch() — поймать её можно только здесь.
    if (isSignalRTransientNoise(error)) {
      return null;
    }

    // Фильтруем отменённые навигации Next.js (NEXT_NOT_FOUND, NEXT_REDIRECT)
    if (error instanceof Error) {
      if (
        error.message === "NEXT_NOT_FOUND" ||
        error.message.startsWith("NEXT_REDIRECT") ||
        error.message === "Route Cancelled" ||
        error.message === "Cancel rendering route"
      ) {
        return null;
      }
    }

    return event;
  },
});

// Тег рантайма: tauri или web
Sentry.setTag("runtime", checkIsTauri() ? "tauri" : "web");

// react-scan: подсветка лишних ре-рендеров прямо в браузере, только в dev.
//
// Место выбрано не случайно: instrumentation-client выполняется раньше
// гидрации, а react-scan должен встроиться в React до первого рендера —
// подключённый из компонента, он пропускает как раз тот рендер, который
// чаще всего и интересен (монтирование дерева).
//
// Гейт написан именно как сравнение с литералом "development": Next
// подставляет сюда строку на этапе сборки, условие схлопывается в `false`, и
// минификатор выбрасывает блок вместе с вызовом import(). В прод-бандле
// react-scan не выполняется; сам пакет — в devDependencies, а после сборки
// его срезает `pnpm prune --prod` в Dockerfile.
//
// Как пользоваться: `pnpm dev` → в углу появляется панель. Обводка вокруг
// компонента = он перерисовался. Интересны не одиночные вспышки, а те, что
// повторяются при действии, которое этот компонент не затрагивает: у нас три
// источника состояния (Zustand, React Query, локальный useState), и каскад
// от одного из них через общий провайдер визуально ничем себя не выдаёт.
if (process.env.NODE_ENV === "development") {
  void import("react-scan").then(({ scan }) => {
    scan({
      enabled: true,
      // Лог в консоль выключен намеренно: DevPerfTracker уже пишет туда про
      // long tasks и рост кучи, а react-scan в режиме log забивает консоль
      // на каждый ре-рендер и топит оба сигнала.
      log: false,
    });
  });
}
